import React from "react";
import {
    HiDocumentText, HiLink, HiPhotograph, HiBookOpen,
    HiAnnotation, HiShieldCheck, HiIdentification, HiChip,
    HiGlobeAlt, HiLockClosed, HiScale, HiClock, HiUserGroup,
    HiShieldExclamation, HiFingerPrint, HiClipboardCheck,
    HiExternalLink
} from "react-icons/hi";

interface DocumentationProps {
    assetDocumentation?: {
        architecture_diagram_url?: string;
        product_documentation_url?: string;
        runbook_url?: string;
        threat_model_url?: string;
        additional_notes?: string;
        documentation_files?: any;
    };
    asset?: any;
}

const InfoSection: React.FC<{
    title: string;
    icon: React.ReactNode;
    children: React.ReactNode
}> = ({ title, icon, children }) => (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] transition-all hover:shadow-lg">
        <div className="px-5 py-4 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#0F172A] flex items-center justify-center text-white">
                {icon}
            </div>
            <h3 className="text-xs font-black text-[#0F172A]">{title}</h3>
        </div>
        <div className="px-5 pb-5 space-y-4">
            {children}
        </div>
    </div>
);

const Badge: React.FC<{
    children: React.ReactNode;
    variant?: 'success' | 'info' | 'secondary'
}> = ({ children, variant = 'info' }) => {
    const variants = {
        success: 'bg-[#E9F7EF] text-[#27AE60]',
        info: 'bg-[#EBF5FF] text-[#3182CE]',
        secondary: 'bg-[#F1F5F9] text-[#64748B]'
    };
    return (
        <span className={`px-3 py-1 rounded-md text-[10px] font-bold tracking-tight ${variants[variant]}`}>
            {children}
        </span>
    );
};

const DetailRow: React.FC<{
    label: string;
    value: React.ReactNode;
    fullWidth?: boolean;
    isBadge?: boolean;
    badgeVariant?: 'success' | 'info' | 'secondary'
}> = ({ label, value, fullWidth = true, isBadge = false, badgeVariant = 'info' }) => {
    const renderValue = () => {
        if (!value) return <span className="text-slate-300 italic font-medium">Not specified</span>;
        if (isBadge) {
            if (Array.isArray(value)) {
                return (
                    <div className="flex flex-wrap gap-2 mt-1.5">
                        {value.map((v, i) => <Badge key={i} variant={badgeVariant}>{v}</Badge>)}
                    </div>
                );
            }
            return <div className="mt-1.5"><Badge variant={badgeVariant}>{value}</Badge></div>;
        }
        return <span className="text-[13px] font-bold text-slate-700 block mt-0.5">{value}</span>;
    };

    return (
        <div className={fullWidth ? "w-full" : "flex-1"}>
            <p className="text-[11px] font-medium text-slate-400 capitalize">{label}</p>
            {renderValue()}
        </div>
    );
};

const Documentation: React.FC<DocumentationProps> = ({ assetDocumentation, asset }) => {
    // Parse documentation_files if it's a string, falling back to asset.documentation_files
    let files = assetDocumentation?.documentation_files || asset?.documentation_files;
    if (typeof files === 'string') {
        try {
            files = JSON.parse(files);
        } catch (e) {
            files = [];
        }
    }

    const renderList = (val: any) => {
        if (!val) return null;
        if (Array.isArray(val)) return val.join(", ");
        if (typeof val === 'string' && val.startsWith('[')) {
            try {
                const parsed = JSON.parse(val);
                return Array.isArray(parsed) ? parsed.join(", ") : val;
            } catch (e) {
                return val;
            }
        }
        return val;
    };

    const renderArtifactLinks = (label: string, value: any, icon: any, bgColor: string, borderColor: string, defaultName: string) => {
        if (!value) return null;
        
        // Parse if string (handling potential JSON array string)
        let items = value;
        if (typeof items === 'string' && items.startsWith('[')) {
            try {
                items = JSON.parse(items);
            } catch (e) {
                items = [items];
            }
        }
        
        if (!Array.isArray(items)) {
            items = [items];
        }

        return items.filter(Boolean).map((item: any, index: number) => {
            const url = typeof item === 'string' ? item : item.url;
            const name = typeof item === 'object' && item.name ? item.name : (items.length > 1 ? `${defaultName} ${index + 1}` : defaultName);
            
            return (
                <a key={`${label}-${index}`} href={url} target="_blank" rel="noopener noreferrer"
                    className={`relative flex items-center justify-between p-5 rounded-xl ${bgColor} border ${borderColor} transition-all hover:shadow-md group`}>
                    <div className="flex items-center gap-4 pr-6">
                        <div className="w-11 h-11 bg-[#1E293B] rounded-xl flex items-center justify-center text-white shadow-lg shrink-0">
                            {icon}
                        </div>
                        <div className="min-w-0">
                            <h4 className="text-[11px] font-bold text-slate-500 tracking-tight truncate">{label}</h4>
                            <span className="text-[13px] font-bold text-slate-700 truncate block">{name}</span>
                        </div>
                    </div>
                    <HiExternalLink className="absolute top-4 right-4 w-4 h-4 text-slate-400 group-hover:text-blue-500 transition-colors" />
                </a>
            );
        });
    };

    return (
        <section className="space-y-6 animate-in fade-in duration-500 pb-10">
            {/* Header Info */}
            <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
                <div className="flex items-center gap-4 mb-8">
                    <div className="p-3 bg-[#D4EDC9] rounded-xl">
                        <HiDocumentText className="w-6 h-6 text-[#4A6741]" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-slate-900 tracking-tight">Technical Documentation & Context</h2>
                        <p className="text-sm font-medium text-slate-400">Comprehensive asset overview for security researchers</p>
                    </div>
                </div>

                {/* Grid Layout for Asset Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Asset Identity & Context */}
                    <InfoSection title="Asset Identity & Context" icon={<HiIdentification className="w-4 h-4" />}>
                        <div className="space-y-6">
                            <DetailRow label="Asset Name" value={asset?.name} />
                            <DetailRow label="Asset Category" value={asset?.category} />
                            <DetailRow label="Primary Types" value={asset?.primary_type} isBadge badgeVariant="success" />
                        </div>
                    </InfoSection>

                    {/* Technical Scope & Lifecycle */}
                    <InfoSection title="Technical Scope & Lifecycle" icon={<HiChip className="w-4 h-4" />}>
                        <div className="space-y-6">
                            <DetailRow label="Included Components" value={renderList(asset?.includes)} />
                            <div className="flex gap-8">
                                <DetailRow label="Lifecycle Status" value={asset?.lifecycle_status} fullWidth={false} isBadge badgeVariant="success" />
                                <DetailRow label="Change Frequency" value={asset?.change_frequency} fullWidth={false} />
                            </div>
                            <div className="flex gap-8">
                                <DetailRow label="Deployment Model" value={asset?.deployment_model} fullWidth={false} />
                                <DetailRow label="Target Audience" value={renderList(asset?.primary_audience)} fullWidth={false} />
                            </div>
                        </div>
                    </InfoSection>

                    {/* Data & Privacy */}
                    <InfoSection title="Data & Privacy" icon={<HiLockClosed className="w-4 h-4" />}>
                        <div className="space-y-6">
                            <div className="flex gap-8">
                                <DetailRow label="Data Classification" value={asset?.data_classification} fullWidth={false} isBadge badgeVariant="info" />
                                <DetailRow label="Data Residency" value={asset?.data_residency} fullWidth={false} />
                            </div>
                            <DetailRow label="Sensitive Data" value={asset?.sensitive_data_description} />
                            <DetailRow label="Compliance Frameworks" value={asset?.compliance_frameworks ? (Array.isArray(asset.compliance_frameworks) ? asset.compliance_frameworks : [asset.compliance_frameworks]) : null} isBadge badgeVariant="secondary" />
                        </div>
                    </InfoSection>

                    {/* Access & Identity */}
                    <InfoSection title="Access & Identity" icon={<HiFingerPrint className="w-4 h-4" />}>
                        <div className="space-y-4">
                            <DetailRow label="Auth Required" value={asset?.authentication_required} />
                            <DetailRow label="Authorization Model" value={asset?.authorization_model} />
                            <DetailRow label="Identity Provider" value={asset?.identity_provider} />
                            <DetailRow label="User Roles" value={asset?.user_roles} />
                        </div>
                    </InfoSection>

                    {/* Security Profile */}
                    <InfoSection title="Security Profile" icon={<HiShieldExclamation className="w-4 h-4" />}>
                        <div className="space-y-4">
                            <DetailRow label="Internet Exposure" value={asset?.internet_exposure} />
                            <DetailRow label="Allowed Testing" value={asset?.allowed_testing_types ? (Array.isArray(asset.allowed_testing_types) ? asset.allowed_testing_types : [asset.allowed_testing_types]) : null} isBadge badgeVariant="secondary" />
                            <DetailRow label="Testing Constraints" value={asset?.testing_constraints} />
                        </div>
                    </InfoSection>

                    {/* Governance & Compliance */}
                    <InfoSection title="Governance & Compliance" icon={<HiScale className="w-4 h-4" />}>
                        <div className="space-y-4">
                            <DetailRow label="Legal Ownership Confirmed" value={asset?.legal_ownership_confirmed ? "Yes" : "No"} />
                            <DetailRow label="Approved By" value={asset?.approved_by} />
                            <DetailRow label="Approval Date" value={asset?.approval_date && new Date(asset.approval_date).toLocaleDateString()} />
                        </div>
                    </InfoSection>
                </div>
            </div>

            {/* Grouped Technical Artifacts & Files */}
            <div className="bg-[#F8FAFC]/50 rounded-3xl border border-slate-200 p-8 shadow-sm">
                <div className="flex items-center justify-between mb-8">
                    <h3 className="text-xl font-extrabold text-[#1a1f37] tracking-tight flex items-center gap-3">
                        <div className="p-2 bg-blue-600 text-white rounded-xl shadow-lg shadow-blue-500/20">
                            <HiLink className="w-5 h-5" />
                        </div>
                        Technical Artifacts & Files
                    </h3>
                </div>

                <div className="space-y-6">
                    {[
                        { 
                            id: 'product_doc', 
                            title: 'Product Documentation', 
                            linkKey: 'product_documentation_url', 
                            linkTitle: 'Documentation',
                            icon: <HiBookOpen className="w-5 h-5" />,
                            defaultLinkName: "View Product Docs",
                            description: "User guides, product specifications, and feature walkthroughs"
                        },
                        { 
                            id: 'architecture', 
                            title: 'Architecture Diagrams', 
                            linkKey: 'architecture_diagram_url', 
                            linkTitle: 'Architecture',
                            icon: <HiChip className="w-5 h-5" />,
                            defaultLinkName: "View Diagram",
                            description: "System design, network diagrams, and infrastructure cloud layouts"
                        },
                        { 
                            id: 'runbook', 
                            title: 'Runbooks & Deployment', 
                            linkKey: 'runbook_url', 
                            linkTitle: 'Runbook',
                            icon: <HiDocumentText className="w-5 h-5" />,
                            defaultLinkName: "View Instructions",
                            description: "Maintenance procedures, deployment guides, and operational tasks"
                        },
                        { 
                            id: 'threat_model', 
                            title: 'Threat Models & Risk', 
                            linkKey: 'threat_model_url', 
                            linkTitle: 'Threat Model',
                            icon: <HiShieldExclamation className="w-5 h-5" />,
                            defaultLinkName: "View Analysis",
                            description: "Security assessments, risk profiles, and attack surface analysis"
                        }
                    ].map((section) => {
                        const links = renderArtifactLinks(
                            section.linkTitle, 
                            assetDocumentation?.[section.linkKey as keyof typeof assetDocumentation] || asset?.[section.linkKey], 
                            section.icon, 
                            "bg-white", 
                            "border-slate-100", 
                            section.defaultLinkName
                        );
                        
                        const sectionFiles = (files || []).filter((f: any) => f.category === section.id);
                        
                        if (!links?.length && !sectionFiles.length) return null;

                        return (
                            <div key={section.id} className="bg-white rounded-2xl border border-slate-100 p-6 transition-all hover:shadow-md">
                                <div className="flex items-start justify-between mb-6">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-slate-50 text-slate-400 rounded-xl flex items-center justify-center border border-slate-100 shadow-sm">
                                            {section.icon}
                                        </div>
                                        <div>
                                            <h4 className="text-base font-extrabold text-slate-800 tracking-tight">{section.title}</h4>
                                            <p className="text-xs font-semibold text-slate-400 mt-0.5">{section.description}</p>
                                        </div>
                                    </div>
                                    <div className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-[10px] font-black uppercase tracking-widest">
                                        Grouped Resource
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {/* Links */}
                                    {links}

                                    {/* Files */}
                                    {sectionFiles.map((file: any, index: number) => {
                                        const fileName = typeof file === 'string' ? file.split('/').pop() : file.name;
                                        const fileUrl = typeof file === 'string' ? file : file.url;
                                        return (
                                            <a key={`file-${index}`} href={fileUrl} target="_blank" rel="noopener noreferrer"
                                                className="flex items-center gap-4 p-5 rounded-xl border border-slate-100 bg-white hover:border-blue-500 hover:shadow-lg hover:shadow-blue-500/5 transition-all group relative">
                                                <div className="w-11 h-11 bg-[#4A6741] rounded-xl flex items-center justify-center text-white shadow-lg shrink-0">
                                                    <HiDocumentText className="w-6 h-6" />
                                                </div>
                                                <div className="min-w-0">
                                                    <h4 className="text-[11px] font-bold text-slate-500 tracking-tight uppercase">External File</h4>
                                                    <span className="text-[13px] font-bold text-slate-700 truncate block">{fileName}</span>
                                                </div>
                                                <HiExternalLink className="absolute top-4 right-4 w-4 h-4 text-slate-400 group-hover:text-blue-500 transition-colors" />
                                            </a>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}

                    {/* Other Attachments */}
                    {(() => {
                        const otherFiles = (files || []).filter((f: any) => !f.category || (f.category !== 'product_doc' && f.category !== 'architecture' && f.category !== 'runbook' && f.category !== 'threat_model'));
                        if (otherFiles.length === 0) return null;

                        return (
                            <div className="bg-white rounded-2xl border border-slate-100 p-6 transition-all hover:shadow-md">
                                <div className="flex items-start justify-between mb-6">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-slate-50 text-slate-400 rounded-xl flex items-center justify-center border border-slate-100 shadow-sm">
                                            <HiAnnotation className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <h4 className="text-base font-extrabold text-slate-800 tracking-tight">Other Attachments</h4>
                                            <p className="text-xs font-semibold text-slate-400 mt-0.5">Miscellaneous files and additional context packages</p>
                                        </div>
                                    </div>
                                    <div className="px-3 py-1 bg-slate-50 text-slate-400 rounded-full text-[10px] font-black uppercase tracking-widest">
                                        General Files
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {otherFiles.map((file: any, index: number) => {
                                        const fileName = typeof file === 'string' ? file.split('/').pop() : file.name;
                                        const fileUrl = typeof file === 'string' ? file : file.url;
                                        return (
                                            <a key={`other-file-${index}`} href={fileUrl} target="_blank" rel="noopener noreferrer"
                                                className="flex items-center gap-4 p-5 rounded-xl border border-slate-100 bg-white hover:border-blue-500 hover:shadow-lg hover:shadow-blue-500/5 transition-all group relative">
                                                <div className="w-11 h-11 bg-slate-100 text-slate-400 rounded-xl flex items-center justify-center shadow-lg shrink-0 group-hover:bg-blue-50 group-hover:text-blue-500 transition-colors">
                                                    <HiDocumentText className="w-6 h-6" />
                                                </div>
                                                <div className="min-w-0">
                                                    <h4 className="text-[11px] font-bold text-slate-500 tracking-tight uppercase">Misc Attachment</h4>
                                                    <span className="text-[13px] font-bold text-slate-700 truncate block">{fileName}</span>
                                                </div>
                                                <HiExternalLink className="absolute top-4 right-4 w-4 h-4 text-slate-400 group-hover:text-blue-500 transition-colors" />
                                            </a>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Additional Notes (Contextual Notes) */}
                    {(assetDocumentation?.additional_notes || asset?.additional_notes) && (
                        <div className="bg-white rounded-2xl border border-slate-100 p-6 transition-all hover:shadow-md">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                <HiAnnotation className="w-4 h-4" /> Contextual Notes
                            </label>
                            <div className="bg-slate-50/50 rounded-xl p-6 border border-slate-100 text-xs font-medium text-slate-600 leading-relaxed whitespace-pre-wrap">
                                {assetDocumentation?.additional_notes || asset?.additional_notes}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default Documentation;
